import { useState, useEffect } from 'react'
import { useTheme } from '../contexts/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import { leaderboardAPI, checkinsAPI, reportsAPI, getApiBaseUrlWithoutApi } from '../config/api'
import 'primeicons/primeicons.css'

interface UserProfileModalProps {
  isOpen: boolean
  onClose: () => void
  userId: number
}

export default function UserProfileModal({ isOpen, onClose, userId }: UserProfileModalProps) {
  const { theme } = useTheme()
  const { user: currentUser } = useAuth()
  const [profile, setProfile] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [weeklyStats, setWeeklyStats] = useState<any[]>([])
  const [checkInCalendar, setCheckInCalendar] = useState<any>({})
  const [selectedCheckIn, setSelectedCheckIn] = useState<any>(null)
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportData, setReportData] = useState({
    report_type: 'cheating',
    title: '',
    description: '',
    evidence_image: null as File | null
  })
  const [submittingReport, setSubmittingReport] = useState(false)
  
  // Kiểm tra xem đây có phải là profile của user hiện tại không
  const isCurrentUser = currentUser && String(currentUser.id) === String(userId)

  useEffect(() => {
    if (isOpen && userId) {
      const fetchProfile = async () => {
        try {
          setLoading(true)
          // Fetch profile data
          const data = await leaderboardAPI.getProfile(userId)
          
          // Xử lý avatar URL để đảm bảo hiển thị đúng
          const API_BASE_URL = getApiBaseUrlWithoutApi()
          if (data.avatar) {
            if (data.avatar.startsWith('/media/')) {
              data.avatar = `${API_BASE_URL}${data.avatar}`
            } else if (!data.avatar.startsWith('http') && !data.avatar.startsWith('/images/')) {
              data.avatar = `${API_BASE_URL}${data.avatar}`
            }
          }

          // Xử lý badges - đảm bảo format đúng
          // Backend trả về badges dạng: [{'id': badge.id, 'name': badge.name, 'icon': badge.icon, 'earned_date': ...}]
          if (data.badges) {
            // Đảm bảo badges là array
            let badgesArray = Array.isArray(data.badges) ? data.badges : (data.badges?.results || data.badges?.data || [])
            
            // Map badges để đảm bảo format đúng
            badgesArray = badgesArray.map((badge: any) => {
              // Nếu badge là object có nested badge (từ UserBadgeSerializer)
              if (badge.badge) {
                return {
                  id: badge.badge.id,
                  name: badge.badge.name,
                  icon: badge.badge.icon || 'pi-star',
                  earned_date: badge.earned_date,
                  is_emoji: badge.badge.is_emoji || false,
                  earned: true
                }
              }
              // Nếu badge đã là flat object (từ profile API)
              return {
                id: badge.id,
                name: badge.name || badge.badge?.name,
                icon: badge.icon || badge.badge?.icon || 'pi-star',
                earned_date: badge.earned_date,
                is_emoji: badge.is_emoji || false,
                earned: badge.earned !== undefined ? badge.earned : true
              }
            })
            .filter((badge: any) => badge.id && badge.name) // Lọc bỏ badges không hợp lệ
            .filter((badge: any) => badge.earned === true) // CHỈ hiển thị huy hiệu đã đạt được
            
            data.badges = badgesArray
          } else {
            data.badges = []
          }
          
          setProfile(data)
          
          // Fetch weekly stats
          // Ưu tiên dùng dữ liệu từ profile API (có sẵn cho cả user hiện tại và user khác)
          if (data.weekly_stats && Array.isArray(data.weekly_stats) && data.weekly_stats.length > 0) {
            setWeeklyStats(data.weekly_stats)
          } else if (isCurrentUser) {
            // Nếu là user hiện tại và profile không có weekly_stats, fetch riêng
            try {
              const stats = await checkinsAPI.getWeeklyStats()
              setWeeklyStats(Array.isArray(stats) ? stats : [])
            } catch (error) {
              console.error('Error fetching weekly stats:', error)
              setWeeklyStats([])
            }
          } else {
            setWeeklyStats([])
          }
          
          // Lấy dữ liệu calendar từ profile
          if (data.check_in_calendar) {
            // Đảm bảo check_in_calendar có đầy đủ thông tin
            const calendar = {
              check_ins: data.check_in_calendar.check_ins || [],
              check_in_details: data.check_in_calendar.check_in_details || {},
              current_month: data.check_in_calendar.current_month || new Date().getMonth() + 1,
              current_year: data.check_in_calendar.current_year || new Date().getFullYear()
            }
            setCheckInCalendar(calendar)
          } else {
            setCheckInCalendar({})
          }
        } catch (error) {
          setProfile(null)
          setWeeklyStats([])
          setCheckInCalendar({})
        } finally {
          setLoading(false)
        }
      }
      fetchProfile()
    }
  }, [isOpen, userId, isCurrentUser])
  
  // Xử lý click vào ngày trong calendar
  const handleCalendarDateClick = (date: number) => {
    const checkInDetails = checkInCalendar?.check_in_details || {}
    const detail = checkInDetails[date]
    if (detail) {
      setSelectedCheckIn({
        date: date,
        ...detail
      })
    }
  }
  
  // Xử lý gửi báo cáo
  const handleSubmitReport = async () => {
    if (!profile || !selectedCheckIn) return
    
    try {
      setSubmittingReport(true)
      
      await reportsAPI.createReport({
        reported_user_id: profile.id,
        report_type: reportData.report_type,
        title: reportData.title || `Báo cáo điểm danh ngày ${selectedCheckIn.date}`,
        description: reportData.description,
        check_in_id: selectedCheckIn.id,
        check_in_date: selectedCheckIn.check_in_date,
        evidence_image: reportData.evidence_image || undefined
      })
      
      // Reset form và đóng modal
      setReportData({
        report_type: 'cheating',
        title: '',
        description: '',
        evidence_image: null
      })
      setShowReportModal(false)
      setSelectedCheckIn(null)
      
      alert('Đã gửi báo cáo thành công! Cảm ơn bạn đã báo cáo.')
    } catch (error: any) {
      console.error('Error submitting report:', error)
      alert(error.response?.data?.message || error.response?.data?.detail || 'Có lỗi xảy ra khi gửi báo cáo. Vui lòng thử lại.')
    } finally {
      setSubmittingReport(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border-2 ${
        theme === 'dark' 
          ? 'bg-black/95 border-gold-500/50' 
          : 'bg-white border-gold-400/50'
      } shadow-2xl`}>
        {/* Close Button */}
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 z-20 w-10 h-10 rounded-full flex items-center justify-center transition ${
            theme === 'dark'
              ? 'bg-gold-500/20 hover:bg-gold-500/30 text-gold-400'
              : 'bg-gold-400/20 hover:bg-gold-400/30 text-gold-600'
          }`}
        >
          <i className="pi pi-times text-lg"></i>
        </button>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-400"></div>
          </div>
        ) : profile ? (
          <div className="p-6 md:p-8">
            {/* Header */}
            <div className="flex flex-col items-center mb-6">
              <div className={`relative w-24 h-24 md:w-32 md:h-32 rounded-full flex items-center justify-center mb-4 overflow-hidden ${
                theme === 'dark' ? 'bg-gold-500/30 border-2 border-gold-500/50' : 'bg-gold-400/30 border-2 border-gold-400/50'
              }`}>
                {profile.avatar ? (
                  <img
                    src={profile.avatar}
                    alt={profile.name || profile.username}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className={`text-4xl md:text-5xl font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                    {(profile.name || profile.username || profile.email || 'U').charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <h2 className={`text-xl md:text-2xl font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {profile.name || profile.username || 'Người dùng'}
              </h2>
              {/* Chỉ hiển thị email nếu là user hiện tại */}
              {isCurrentUser && profile.email && (
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {profile.email}
                </p>
              )}
              {/* Hiển thị thông tin giới thiệu (bio) nếu có và không phải user hiện tại */}
              {!isCurrentUser && profile.bio && (
                <p className={`text-sm mt-2 px-4 py-2 rounded-lg ${theme === 'dark' ? 'text-gray-300 bg-gray-900/50' : 'text-gray-700 bg-gray-100'}`}>
                  {profile.bio}
                </p>
              )}
              {/* Hiển thị bio cho user hiện tại nếu có */}
              {isCurrentUser && profile.bio && (
                <p className={`text-sm mt-2 px-4 py-2 rounded-lg ${theme === 'dark' ? 'text-gray-300 bg-gray-900/50' : 'text-gray-700 bg-gray-100'}`}>
                  {profile.bio}
                </p>
              )}
            </div>

            {/* Stats */}
            <div className={`grid grid-cols-3 gap-4 mb-6 p-4 rounded-xl ${
              theme === 'dark' ? 'bg-gold-900/20' : 'bg-gold-100/50'
            }`}>
              <div className="text-center">
                <div className={`text-2xl md:text-3xl font-bold mb-1 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                  {profile.points !== undefined ? profile.points : (profile.total_points !== undefined ? profile.total_points : 0)}
                </div>
                <div className={`text-xs md:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Điểm
                </div>
              </div>
              <div className="text-center">
                <div className={`text-2xl md:text-3xl font-bold mb-1 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-500'}`}>
                  {profile.max_streak !== undefined ? profile.max_streak : (profile.streak !== undefined ? profile.streak : 0)}
                </div>
                <div className={`text-xs md:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Chuỗi max
                </div>
              </div>
              <div className="text-center">
                <div className={`text-2xl md:text-3xl font-bold mb-1 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                  {profile.rank !== undefined && profile.rank !== null ? profile.rank : '-'}
                </div>
                <div className={`text-xs md:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Hạng
                </div>
              </div>
            </div>

            {/* Badges Section */}
            <div className="mb-6">
              <h3 className={`text-lg font-bold mb-4 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                Huy Hiệu {profile.badges && profile.badges.length > 0 && `(${profile.badges.length})`}
              </h3>
              {profile.badges && profile.badges.length > 0 ? (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                  {profile.badges.map((badge: any) => (
                    <div
                      key={badge.id}
                      className={`p-3 rounded-lg border-2 text-center transition-transform hover:scale-105 ${
                        theme === 'dark'
                          ? 'bg-black/50 border-gold-500/30 hover:border-gold-500/50'
                          : 'bg-gray-100 border-gold-400/30 hover:border-gold-400/50'
                      }`}
                    >
                      <div className="mb-2">
                        {badge.is_emoji ? (
                          <span className="text-2xl md:text-3xl block">{badge.icon}</span>
                        ) : (
                          <i className={`pi ${badge.icon || 'pi-star'} text-2xl md:text-3xl text-yellow-400`}></i>
                        )}
                      </div>
                      <div className={`text-xs font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {badge.name}
                      </div>
                      {badge.earned_date && (
                        <div className={`text-[10px] mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>
                          {new Date(badge.earned_date).toLocaleDateString('vi-VN')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`p-6 rounded-lg text-center ${theme === 'dark' ? 'bg-black/50 border border-gold-500/20' : 'bg-gray-100 border border-gold-400/20'}`}>
                  <i className={`pi pi-star text-3xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}></i>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Chưa có huy hiệu nào
                  </p>
                </div>
              )}
            </div>

            {/* Weekly Training Chart */}
            <div className="mb-6">
              <h3 className={`text-lg font-bold mb-4 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                7 ngày luyện tập gần nhất
              </h3>
              {weeklyStats && Array.isArray(weeklyStats) && weeklyStats.length > 0 ? (
                <div className={`p-4 md:p-5 overflow-hidden ${theme === 'dark' ? 'bg-black/50' : 'bg-gray-100'}`}>
                  <div className="flex items-end justify-between gap-0.5 md:gap-1 h-40 md:h-48 px-1">
                    {weeklyStats.map((stat: any, index: number) => {
                      // Parse date string an toàn để tránh lệch timezone
                      // Backend trả về ISO date string (ví dụ: "2024-01-15")
                      let date: Date
                      if (stat.date) {
                        // Nếu là ISO date string (YYYY-MM-DD), parse theo local timezone
                        if (typeof stat.date === 'string' && stat.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                          const [year, month, day] = stat.date.split('-').map(Number)
                          date = new Date(year, month - 1, day) // month - 1 vì Date month bắt đầu từ 0
                        } else {
                          date = new Date(stat.date)
                        }
                      } else {
                        date = new Date()
                      }
                      const dayName = date.toLocaleDateString('vi-VN', { weekday: 'short' })
                      const dayNumber = date.getDate()
                      const value = stat.duration || stat.minutes || stat.checkins || 0
                      const maxValue = Math.max(...weeklyStats.map((s: any) => s.duration || s.minutes || s.checkins || 0), 1)
                      const height = maxValue > 0 ? (value / maxValue) * 100 : 0
                      
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center gap-1 min-w-0 max-w-full">
                          <div className="relative w-full flex items-end justify-center mb-1" style={{ height: '140px' }}>
                            <div
                              className={`w-full max-w-full transition-all hover:opacity-80 ${
                                theme === 'dark' ? 'bg-gold-gradient' : 'bg-gold-gradient'
                              }`}
                              style={{ height: `${height}%`, minHeight: value > 0 ? '4px' : '0' }}
                              title={`${dayName} ${dayNumber}: ${value} ${stat.duration || stat.minutes ? 'phút' : 'buổi'}`}
                            />
                          </div>
                          <div className="text-center w-full min-w-0 px-0.5">
                            <div className={`text-[9px] md:text-[10px] font-semibold leading-tight ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {dayName}
                            </div>
                            <div className={`text-[8px] md:text-[9px] leading-tight mt-0.5 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>
                              {dayNumber}
                            </div>
                            <div className={`text-[9px] md:text-[10px] font-bold mt-1 leading-tight ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}>
                              {value}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className={`text-[10px] md:text-xs text-center mt-3 pt-3 border-t ${theme === 'dark' ? 'border-gold-500/20 text-gray-400' : 'border-gold-400/20 text-gray-600'}`}>
                    {weeklyStats[0]?.duration || weeklyStats[0]?.minutes ? 'Số phút tập luyện' : 'Số buổi tập luyện'}
                  </div>
                </div>
              ) : (
                <div className={`p-6 rounded-lg text-center ${theme === 'dark' ? 'bg-black/50 border border-gold-500/20' : 'bg-gray-100 border border-gold-400/20'}`}>
                  <i className={`pi pi-chart-line text-3xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}></i>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Chưa có dữ liệu luyện tập 7 ngày luyện tập gần nhất
                  </p>
                </div>
              )}
            </div>

            {/* Calendar Section */}
            {checkInCalendar && (checkInCalendar.check_ins || (Array.isArray(checkInCalendar.check_ins) && checkInCalendar.check_ins.length > 0) || Object.keys(checkInCalendar.check_in_details || {}).length > 0) && (
              <div className="mb-6">
                <h3 className={`text-lg font-bold mb-4 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                  Lịch Điểm Danh - tháng {checkInCalendar.current_month || new Date().getMonth() + 1}/{checkInCalendar.current_year || new Date().getFullYear()}
                </h3>
                <CalendarDisplay 
                  checkInDates={Array.isArray(checkInCalendar.check_ins) ? checkInCalendar.check_ins : (checkInCalendar.check_in_details ? Object.keys(checkInCalendar.check_in_details).map(d => parseInt(d, 10)) : [])}
                  checkInDetails={checkInCalendar.check_in_details || {}}
                  currentMonth={checkInCalendar.current_month || new Date().getMonth() + 1}
                  currentYear={checkInCalendar.current_year || new Date().getFullYear()}
                  onDateClick={handleCalendarDateClick}
                  theme={theme}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
              Không tìm thấy thông tin người dùng
            </p>
          </div>
        )}
      </div>
      
      {/* Check-in Detail Modal */}
      {selectedCheckIn && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedCheckIn(null)}
          />
          <div className={`relative z-10 w-full max-w-md rounded-2xl border-2 ${
            theme === 'dark' 
              ? 'bg-black/95 border-gold-500/50' 
              : 'bg-white border-gold-400/50'
          } shadow-2xl p-6`}>
            <button
              onClick={() => setSelectedCheckIn(null)}
              className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition ${
                theme === 'dark'
                  ? 'bg-gold-500/20 hover:bg-gold-500/30 text-gold-400'
                  : 'bg-gold-400/20 hover:bg-gold-400/30 text-gold-600'
              }`}
            >
              <i className="pi pi-times text-sm"></i>
            </button>
            <h3 className={`text-xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Chi tiết điểm danh ngày {selectedCheckIn.date}
            </h3>
            {selectedCheckIn.image && (
              <div className="mb-4">
                <img 
                  src={selectedCheckIn.image.startsWith('http') 
                    ? selectedCheckIn.image 
                    : `${getApiBaseUrlWithoutApi()}${selectedCheckIn.image}`
                  }
                  alt={`Check-in ngày ${selectedCheckIn.date}`}
                  className="w-full rounded-lg"
                />
              </div>
            )}
            <div className={`space-y-2 text-sm mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              <div>
                <span className="font-semibold">Ngày:</span> {selectedCheckIn.check_in_date ? (() => {
                  // Parse date string an toàn để tránh lệch timezone
                  const dateStr = selectedCheckIn.check_in_date
                  let date: Date
                  if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}(T|$)/)) {
                    // ISO date string (YYYY-MM-DD hoặc YYYY-MM-DDTHH:mm:ss)
                    const datePart = dateStr.split('T')[0]
                    const [year, month, day] = datePart.split('-').map(Number)
                    date = new Date(year, month - 1, day) // month - 1 vì Date month bắt đầu từ 0
                  } else {
                    date = new Date(dateStr)
                  }
                  return date.toLocaleDateString('vi-VN')
                })() : `Ngày ${selectedCheckIn.date}`}
              </div>
              {selectedCheckIn.check_in_time && (
                <div>
                  <span className="font-semibold">Giờ:</span> {selectedCheckIn.check_in_time}
                </div>
              )}
              {selectedCheckIn.notes && (
                <div>
                  <span className="font-semibold">Ghi chú:</span> {selectedCheckIn.notes}
                </div>
              )}
            </div>
            
            {/* Nút Gửi báo cáo - chỉ hiển thị khi xem profile user khác */}
            {!isCurrentUser && (
              <button
                onClick={() => setShowReportModal(true)}
                className="w-full py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center justify-center gap-2 text-sm font-semibold"
              >
                <i className="pi pi-flag text-sm"></i>
                <span>Gửi báo cáo</span>
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Report Modal */}
      {showReportModal && selectedCheckIn && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowReportModal(false)}
          />
          <div className={`relative z-10 w-full max-w-lg rounded-2xl border-2 ${
            theme === 'dark' 
              ? 'bg-black/95 border-gold-500/50' 
              : 'bg-white border-gold-400/50'
          } shadow-2xl p-6 max-h-[90vh] overflow-y-auto`}>
            <button
              onClick={() => setShowReportModal(false)}
              className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition ${
                theme === 'dark'
                  ? 'bg-gold-500/20 hover:bg-gold-500/30 text-gold-400'
                  : 'bg-gold-400/20 hover:bg-gold-400/30 text-gold-600'
              }`}
            >
              <i className="pi pi-times text-sm"></i>
            </button>
            <h3 className={`text-xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Gửi báo cáo
            </h3>
            
            <div className="space-y-4">
              {/* Loại báo cáo */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Loại báo cáo *
                </label>
                <select
                  value={reportData.report_type}
                  onChange={(e) => setReportData({ ...reportData, report_type: e.target.value })}
                  className={`w-full px-4 py-2 rounded-lg border-2 ${
                    theme === 'dark' 
                      ? 'bg-black border-gold-500/30 text-white' 
                      : 'bg-white border-gold-400/30 text-gray-900'
                  } focus:outline-none focus:border-gold-400`}
                >
                  <option value="cheating">Gian lận điểm danh</option>
                  <option value="inappropriate_content">Nội dung không phù hợp</option>
                  <option value="harassment">Quấy rối</option>
                  <option value="other">Khác</option>
                </select>
              </div>
              
              {/* Tiêu đề */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Tiêu đề
                </label>
                <input
                  type="text"
                  value={reportData.title}
                  onChange={(e) => setReportData({ ...reportData, title: e.target.value })}
                  placeholder="Nhập tiêu đề báo cáo (tùy chọn)"
                  className={`w-full px-4 py-2 rounded-lg border-2 ${
                    theme === 'dark' 
                      ? 'bg-black border-gold-500/30 text-white' 
                      : 'bg-white border-gold-400/30 text-gray-900'
                  } focus:outline-none focus:border-gold-400`}
                />
              </div>
              
              {/* Mô tả */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Mô tả chi tiết *
                </label>
                <textarea
                  value={reportData.description}
                  onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
                  placeholder="Mô tả chi tiết về vấn đề bạn muốn báo cáo..."
                  rows={4}
                  required
                  className={`w-full px-4 py-2 rounded-lg border-2 ${
                    theme === 'dark' 
                      ? 'bg-black border-gold-500/30 text-white' 
                      : 'bg-white border-gold-400/30 text-gray-900'
                  } focus:outline-none focus:border-gold-400 resize-none`}
                />
              </div>
              
              {/* Hình ảnh bằng chứng */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Hình ảnh bằng chứng (tùy chọn)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      setReportData({ ...reportData, evidence_image: file })
                    }
                  }}
                  className={`w-full px-4 py-2 rounded-lg border-2 ${
                    theme === 'dark' 
                      ? 'bg-black border-gold-500/30 text-white' 
                      : 'bg-white border-gold-400/30 text-gray-900'
                  } focus:outline-none focus:border-gold-400`}
                />
                {reportData.evidence_image && (
                  <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Đã chọn: {reportData.evidence_image.name}
                  </p>
                )}
              </div>
              
              {/* Nút gửi */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className={`flex-1 py-2 px-4 rounded-lg transition ${
                    theme === 'dark' 
                      ? 'bg-gray-700 text-white hover:bg-gray-600' 
                      : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                  }`}
                >
                  Hủy
                </button>
                <button
                  onClick={handleSubmitReport}
                  disabled={!reportData.description || submittingReport}
                  className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submittingReport ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Đang gửi...</span>
                    </>
                  ) : (
                    <>
                      <i className="pi pi-send text-sm"></i>
                      <span>Gửi báo cáo</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Calendar Component
function CalendarDisplay({ 
  checkInDates, 
  checkInDetails, 
  currentMonth, 
  currentYear, 
  onDateClick, 
  theme 
}: {
  checkInDates: number[]
  checkInDetails: any
  currentMonth: number
  currentYear: number
  onDateClick: (date: number) => void
  theme: string
}) {
  const daysInMonth = new Date(currentYear, currentMonth, 0).getDate()
  const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1).getDay()
  
  return (
    <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-black/50 border border-gold-500/20' : 'bg-gray-100 border border-gold-400/20'}`}>
      {/* Days of Week Header */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'].map((day) => (
          <div
            key={day}
            className={`text-center text-xs font-semibold py-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Dates */}
      <div className="grid grid-cols-7 gap-1">
        {/* Empty cells for days before first day of month */}
        {Array.from({ length: firstDayOfMonth }).map((_, i) => (
          <div key={`empty-${i}`} className="aspect-square"></div>
        ))}

        {/* Date cells */}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const date = i + 1
          const today = new Date()
          const isToday = today.getDate() === date && today.getMonth() + 1 === currentMonth && today.getFullYear() === currentYear
          const isCheckedIn = checkInDates.includes(date)
          const hasDetail = checkInDetails[date]

          return (
            <div
              key={date}
              onClick={() => hasDetail && onDateClick(date)}
              className={`aspect-square flex items-center justify-center rounded-lg text-xs font-semibold transition ${
                isCheckedIn
                  ? 'bg-green-500 text-white cursor-pointer hover:bg-green-600'
                  : isToday
                  ? `${theme === 'dark' ? 'bg-gold-500/30 border-2 border-gold-500' : 'bg-gold-400/30 border-2 border-gold-400'} ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`
                  : theme === 'dark'
                  ? 'text-white hover:bg-gray-800'
                  : 'text-gray-900 hover:bg-gray-100'
              }`}
            >
              {date}
            </div>
          )
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 flex-wrap mt-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded"></div>
          <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            Đã điểm danh
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded ${theme === 'dark' ? 'bg-gold-500/30 border-2 border-gold-500' : 'bg-gold-400/30 border-2 border-gold-400'}`}></div>
          <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            Hôm nay
          </span>
        </div>
      </div>
    </div>
  )
}

